import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Holds the user on the page while it has unsaved changes: an in-app navigation is paused for a
 * confirm prompt (React Router's data-router blocker), and closing or refreshing the tab gets the
 * browser's own "leave site?" warning.
 *
 * `isDirty` is a function read at navigation time, not a value: a handler that saves and then
 * navigates in the same tick must be let through, and React state from that save would not be
 * visible to the blocker until the next render.
 *
 * Render the prompt while `blocked` is true and answer it with `proceed` (leave) or `stay`.
 */
export const useUnsavedChangesGuard = (isDirty: () => boolean) => {
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current() &&
      currentLocation.pathname + currentLocation.search !==
        nextLocation.pathname + nextLocation.search,
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirtyRef.current()) return;
      event.preventDefault();
      // Still required by Chrome/Edge to show the prompt; the text itself is ignored.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const proceed = useCallback(() => blocker.proceed?.(), [blocker]);
  const stay = useCallback(() => blocker.reset?.(), [blocker]);

  return { blocked: blocker.state === "blocked", proceed, stay };
};
